import React, { useEffect, useState } from 'react';
import { Form as FinalForm, FormSpy } from 'react-final-form';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { numberAtLeast, required } from '../../../util/validators';
import { PURCHASE_PROCESS_NAME } from '../../../transactions/transaction';
import { logAddToCart, logCartError, CART_ACTIONS } from '../../../util/cartLogging';
import {
  getEstimatedDeliveryDate,
  getShippingMethod,
  calculateShippingPrice,
  parseShippingMethods,
  addBusinessDays,
} from '../../../util/dates';
import DeliveryConditionsPanel from '../../../containers/ListingPage/DeliveryConditionsPanel';

import Form from '../../Form/Form';
import FieldSelect from '../../FieldSelect/FieldSelect';
import FieldTextInput from '../../FieldTextInput/FieldTextInput';
import { PrimaryButton } from '../../Button/Button';
import { H3, H6 } from '../../Heading/Heading';
import NamedLink from '../../NamedLink/NamedLink';
import VariantSelector from '../../VariantSelector/VariantSelector';

import deliveryIcon from '../../../assets/delivery-icon.png';

import EstimatedCustomerBreakdownMaybe from '../EstimatedCustomerBreakdownMaybe';

import FetchLineItemsError from '../FetchLineItemsError/FetchLineItemsError.js';

import css from './ProductOrderForm.module.css';

/** Toujours false : on n'utilise plus publicData.review / publicData.reviews pour le lien « Voir les avis ». */
const hasInternetReviewFromListing = () => false;

// Browsers can't render huge number of select options.
// (stock is shown inside select element)
// Note: input element could allow ordering bigger quantities
const MAX_QUANTITY_FOR_DROPDOWN = 100;

// handleFetchLineItems function will be defined inside the component where it has access to mounted state

const DeliveryMethodMaybe = props => {
  const {
    displayDeliveryMethod,
    hasMultipleDeliveryMethods,
    deliveryMethod,
    hasStock,
    formId,
    intl,
  } = props;
  const showDeliveryMethodSelector = displayDeliveryMethod && hasMultipleDeliveryMethods;
  const showSingleDeliveryMethod = displayDeliveryMethod && deliveryMethod;
  return !hasStock ? null : showDeliveryMethodSelector ? (
    <FieldSelect
      id={`${formId}.deliveryMethod`}
      className={css.deliveryField}
      name="deliveryMethod"
      label={intl.formatMessage({ id: 'ProductOrderForm.deliveryMethodLabel' })}
      validate={required(intl.formatMessage({ id: 'ProductOrderForm.deliveryMethodRequired' }))}
    >
      <option disabled value="">
        {intl.formatMessage({ id: 'ProductOrderForm.selectDeliveryMethodOption' })}
      </option>
      <option value={'pickup'}>
        {intl.formatMessage({ id: 'ProductOrderForm.pickupOption' })}
      </option>
      <option value={'shipping'}>
        {intl.formatMessage({ id: 'ProductOrderForm.shippingOption' })}
      </option>
    </FieldSelect>
  ) : showSingleDeliveryMethod ? (
    <div className={css.deliveryField}>
      <H3 rootClassName={css.singleDeliveryMethodLabel}>
        {intl.formatMessage({ id: 'ProductOrderForm.deliveryMethodLabel' })}
      </H3>
      <p className={css.singleDeliveryMethodSelected}>
        {deliveryMethod === 'shipping'
          ? intl.formatMessage({ id: 'ProductOrderForm.shippingOption' })
          : intl.formatMessage({ id: 'ProductOrderForm.pickupOption' })}
      </p>
      <FieldTextInput
        id={`${formId}.deliveryMethod`}
        className={css.deliveryField}
        name="deliveryMethod"
        type="hidden"
      />
    </div>
  ) : null;
};

// Shipping method selector component
const ShippingMethodSelector = props => {
  const { listing, formId, deliveryMethod, quantity, intl, price } = props;

  const shippingMethodsRaw = listing?.attributes?.publicData?.shippingMethods;
  const shippingMethods = parseShippingMethods(shippingMethodsRaw);
  const hasShippingMethods = shippingMethods.length > 0;

  // Only show if delivery method is shipping
  if (deliveryMethod !== 'shipping') {
    return null;
  }

  // Use legacy shipping prices if no shippingMethods defined (backward compatibility)
  if (!hasShippingMethods) {
    return null;
  }

  // Check if price and currency are available
  if (!price || !price.currency) {
    return null;
  }

  const { currency } = price;

  return (
    <div className={css.shippingMethodField}>
      <FieldSelect
        id={`${formId}.shippingMethod`}
        name="shippingMethod"
        label={intl.formatMessage({ id: 'ProductOrderForm.shippingMethodLabel' })}
        validate={required(intl.formatMessage({ id: 'ProductOrderForm.shippingMethodRequired' }))}
      >
        <option disabled value="">
          {intl.formatMessage({ id: 'ProductOrderForm.selectShippingMethodOption' })}
        </option>
        {shippingMethods.map(method => {
          const shippingPrice = calculateShippingPrice(method, quantity || 1);
          const formattedPrice = intl.formatNumber(shippingPrice / 100, {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });

          return (
            <option key={method.id} value={method.id}>
              {method.name} - {formattedPrice} ({method.delay} jours ouvrés)
            </option>
          );
        })}
      </FieldSelect>
    </div>
  );
};

// Estimated delivery date component
const EstimatedDeliveryDate = props => {
  const { listing, shippingMethodId, intl } = props;

  const selectedMethod = getShippingMethod(listing, shippingMethodId);

  if (!selectedMethod || !selectedMethod.delay) {
    return null;
  }

  const deliveryDate = getEstimatedDeliveryDate(parseInt(selectedMethod.delay, 10), intl);

  if (!deliveryDate) {
    return null;
  }

  return (
    <div className={css.estimatedDelivery}>
      <FormattedMessage
        id="ProductOrderForm.estimatedDeliveryDate"
        values={{ date: deliveryDate }}
      />
    </div>
  );
};

// Custom quantity selector component
const QuantitySelector = props => {
  const { quantity, onQuantityChange, maxQuantity, disabled } = props;

  const handleIncrement = () => {
    if (quantity < maxQuantity && !disabled) {
      onQuantityChange(quantity + 1);
    }
  };

  const handleDecrement = () => {
    if (quantity > 1 && !disabled) {
      onQuantityChange(quantity - 1);
    }
  };

  return (
    <div className={css.quantitySelector}>
      <button
        type="button"
        className={css.quantityButton}
        onClick={handleDecrement}
        disabled={quantity <= 1 || disabled}
        aria-label="Decrease quantity"
      >
        <span className={css.quantityButtonText}>−</span>
      </button>
      <span className={css.quantityDisplay}>{quantity}</span>
      <button
        type="button"
        className={css.quantityButton}
        onClick={handleIncrement}
        disabled={quantity >= maxQuantity || disabled}
        aria-label="Increase quantity"
      >
        <span className={css.quantityButtonText}>+</span>
      </button>
    </div>
  );
};

// Shipping information component
const ShippingInfo = props => {
  const { publicData, intl, price } = props;
  const {
    shippingPriceInSubunitsOneItem,
    shippingPriceInSubunitsAdditionalItems,
    shippingConditions,
  } = publicData || {};

  if (!shippingPriceInSubunitsOneItem && !shippingPriceInSubunitsAdditionalItems) {
    return null;
  }

  const formatShippingPrice = priceInSubunits => {
    if (!priceInSubunits) return null;
    // Convert subunits to currency (assuming 100 subunits = 1 currency unit)
    const priceValue = priceInSubunits / 100;
    const currency = price?.currency || 'USD';
    return intl.formatNumber(priceValue, { style: 'currency', currency });
  };

  const firstItemPrice = formatShippingPrice(shippingPriceInSubunitsOneItem);
  const additionalItemPrice = formatShippingPrice(shippingPriceInSubunitsAdditionalItems);

  return (
    <div className={css.shippingInfo}>
      <h4 className={css.shippingTitle}>
        <FormattedMessage
          id="ProductOrderForm.shippingInfoTitle"
          defaultMessage="Shipping Information"
        />
      </h4>
      <div className={css.shippingDetails}>
        {firstItemPrice && (
          <p className={css.shippingPrice}>
            <FormattedMessage
              id="ProductOrderForm.shippingFirstItem"
              defaultMessage="First item: {price}"
              values={{ price: firstItemPrice }}
            />
          </p>
        )}
        {additionalItemPrice && (
          <p className={css.shippingPrice}>
            <FormattedMessage
              id="ProductOrderForm.shippingAdditionalItems"
              defaultMessage="Additional items: {price} each"
              values={{ price: additionalItemPrice }}
            />
          </p>
        )}
        {shippingConditions && <p className={css.shippingConditions}>{shippingConditions}</p>}
      </div>
    </div>
  );
};

const DeliveryMethodsDisplay = props => {
  const { listing, price, intl } = props;

  const [conditionsPanelOpen, setConditionsPanelOpen] = useState(false);

  const shippingMethodsRaw = listing?.attributes?.publicData?.shippingMethods;
  const shippingMethods = parseShippingMethods(shippingMethodsRaw);

  if (!shippingMethods || shippingMethods.length === 0) {
    return null;
  }

  const delays = shippingMethods.map(m => parseInt(m.delay, 10)).filter(d => d > 0);
  if (delays.length === 0) {
    return null;
  }

  const shortestDelay = Math.min(...delays);
  const fastestMethod = shippingMethods.find(m => parseInt(m.delay, 10) === shortestDelay);
  const listingPriceAmount = price?.amount || 0;
  const currency = price?.currency || 'EUR';
  const shippingPrice = fastestMethod ? calculateShippingPrice(fastestMethod, 1) : 0;
  const franco = fastestMethod?.franco || 0;
  const isFreeShipping =
    shippingPrice === 0 ||
    (typeof franco === 'number' && franco > 0 && listingPriceAmount >= franco);
  const shippingLabel = isFreeShipping
    ? 'Livraison gratuite'
    : `${intl.formatNumber(shippingPrice / 100, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} de livraison`;

  const deliveryDate = addBusinessDays(new Date(), shortestDelay);
  const formattedDate = intl.formatDate(deliveryDate, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  return (
    <div className={css.deliveryContainer}>
      <div
        className={css.deliveryMethodsDisplay}
        role="button"
        tabIndex={0}
        onClick={() => setConditionsPanelOpen(true)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setConditionsPanelOpen(true);
          }
        }}
      >
        <div className={css.deliveryTextContainer}>
          <div className={css.deliveryRow}>
            <img src={deliveryIcon} alt="Livraison" className={css.deliveryIcon} />
            <p className={css.deliveryText}>
              <strong>Chez vous le {formattedDate}</strong>
              {` (${shippingLabel})`}
            </p>
          </div>
          <div className={css.deliveryRow}>
            <svg
              className={css.deliveryIcon}
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M4 8L3.64645 8.35355L3.29289 8L3.64645 7.64645L4 8ZM9 19.5C8.72386 19.5 8.5 19.2761 8.5 19C8.5 18.7239 8.72386 18.5 9 18.5L9 19.5ZM8.64645 13.3536L3.64645 8.35355L4.35355 7.64645L9.35355 12.6464L8.64645 13.3536ZM3.64645 7.64645L8.64645 2.64645L9.35355 3.35355L4.35355 8.35355L3.64645 7.64645ZM4 7.5L14.5 7.5L14.5 8.5L4 8.5L4 7.5ZM14.5 19.5L9 19.5L9 18.5L14.5 18.5L14.5 19.5ZM20.5 13.5C20.5 16.8137 17.8137 19.5 14.5 19.5L14.5 18.5C17.2614 18.5 19.5 16.2614 19.5 13.5L20.5 13.5ZM14.5 7.5C17.8137 7.5 20.5 10.1863 20.5 13.5L19.5 13.5C19.5 10.7386 17.2614 8.5 14.5 8.5L14.5 7.5Z"
                fill="#222222"
              />
            </svg>
            <p className={css.deliveryText}>14 jours satisfait ou remboursé</p>
          </div>
          <div className={css.deliveryRow}>
            <svg
              className={css.deliveryIcon}
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <rect
                fill="none"
                stroke="#020202"
                strokeMiterlimit="10"
                strokeWidth="1.1"
                x="6.27"
                y="5.32"
                width="11.45"
                height="15.27"
                rx="5.73"
              />
              <path
                fill="none"
                stroke="#020202"
                strokeMiterlimit="10"
                strokeWidth="1.1"
                d="M17.73,9.14h1.91A2.86,2.86,0,0,1,22.5,12v1.91a2.86,2.86,0,0,1-2.86,2.86H17.73a0,0,0,0,1,0,0V9.14A0,0,0,0,1,17.73,9.14Z"
              />
              <path
                fill="none"
                stroke="#020202"
                strokeMiterlimit="10"
                strokeWidth="1.1"
                d="M1.5,9.14H3.41A2.86,2.86,0,0,1,6.27,12v1.91a2.86,2.86,0,0,1-2.86,2.86H1.5a0,0,0,0,1,0,0V9.14A0,0,0,0,1,1.5,9.14Z"
                transform="translate(7.77 25.91) rotate(180)"
              />
              <path
                fill="none"
                stroke="#020202"
                strokeMiterlimit="10"
                strokeWidth="1.1"
                d="M4.36,9.14h0A7.64,7.64,0,0,1,12,1.5h0a7.64,7.64,0,0,1,7.64,7.64h0"
              />
              <path
                fill="none"
                stroke="#020202"
                strokeMiterlimit="10"
                strokeWidth="1.1"
                d="M19.64,16.77v1a4.78,4.78,0,0,1-4.78,4.77"
              />
            </svg>
            <div className={css.deliverySupportInline}>
              <p className={css.deliveryText}>Service client Neokasa</p>
              <svg
                viewBox="0 0 36 36"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
                role="img"
                className={css.deliveryFlagFrEnd}
                preserveAspectRatio="xMidYMid meet"
              >
                <path fill="#f53e28" d="M36 27a4 4 0 0 1-4 4h-8V5h8a4 4 0 0 1 4 4v18z" />
                <path fill="#68abee" d="M4 5a4 4 0 0 0-4 4v18a4 4 0 0 0 4 4h8V5H4z" />
                <path fill="#ffffff" d="M12 5h12v26H12z" />
              </svg>
              <a className={css.deliveryText}>Appelez-nous au 01 89 48 06 12</a>
            </div>
          </div>
        </div>
        <div className={css.deliveryNoteContainer}>
          <p className={css.deliveryNote}>
            <button
              type="button"
              className={css.deliveryConditionsLink}
              onClick={e => {
                e.stopPropagation();
                setConditionsPanelOpen(true);
              }}
            >
              Voir les conditions de livraison, retour et garantie
            </button>
          </p>
        </div>
      </div>
      <DeliveryConditionsPanel
        isOpen={conditionsPanelOpen}
        onClose={() => setConditionsPanelOpen(false)}
        listing={listing}
        price={price}
        intl={intl}
      />
    </div>
  );
};

// Reviews count component
const ReviewsCount = props => {
  const { reviews, intl, hasInternetReview } = props;
  const reviewsCount = Array.isArray(reviews) ? reviews.length : 0;

  if (reviewsCount === 0 && !hasInternetReview) {
    return null;
  }

  const handleReviewsClick = () => {
    const reviewsSection = document.getElementById('reviews-section');
    if (reviewsSection) {
      reviewsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className={css.reviewsCount}>
      <button type="button" className={css.reviewsLink} onClick={handleReviewsClick}>
        <FormattedMessage
          id="ProductOrderForm.reviewsCount"
          defaultMessage="Voir les avis"
          values={{ count: reviewsCount }}
        />
      </button>
    </div>
  );
};

// Vendor information component
const VendorInfo = props => {
  const { author, onVendorClick } = props;

  if (!author) {
    return null;
  }

  const authorDisplayName = author.attributes?.profile?.displayName || 'Vendor';

  return (
    <div className={css.vendorInfo}>
      <span className={css.vendorLabel}>
        <FormattedMessage id="ProductOrderForm.soldBy" defaultMessage="Sold by" />
      </span>
      {onVendorClick ? (
        <button type="button" className={css.vendorLinkButton} onClick={onVendorClick}>
          {authorDisplayName}
        </button>
      ) : (
        <NamedLink name="ProfilePage" params={{ id: author.id?.uuid }} className={css.vendorLink}>
          {authorDisplayName}
        </NamedLink>
      )}
    </div>
  );
};

const renderForm = formRenderProps => {
  const [mounted, setMounted] = useState(false);
  const [localQuantity, setLocalQuantity] = useState(1);
  const [fetchTimeout, setFetchTimeout] = useState(null); // Add timeout state for debouncing
  const {
    // FormRenderProps from final-form
    handleSubmit,
    form: formApi,

    // Custom props passed to the form component
    intl,
    formId,
    currentStock,
    allowOrdersOfMultipleItems,
    hasMultipleDeliveryMethods,
    displayDeliveryMethod,
    listingId,
    isOwnListing,
    onFetchTransactionLineItems,
    onSubmit,
    onContactUser,
    onOpenSellerPanel,
    onOpenLoyaltyPanel,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    price,
    payoutDetailsWarning,
    marketplaceName,
    values,
    listing,
    reviews,
    shippingEnabled,
    pickupEnabled,
  } = formRenderProps;

  // Define handleFetchLineItems function inside the component where it has access to mounted state
  const handleFetchLineItems = ({
    quantity,
    deliveryMethod,
    shippingMethod,
    displayDeliveryMethod,
    listingId,
    isOwnListing,
    fetchLineItemsInProgress,
    onFetchTransactionLineItems,
    immediate = false,
  }) => {
    // Don't make API calls if component is unmounting
    if (!mounted) {
      return;
    }

    const stockReservationQuantity = Number.parseInt(quantity, 10);
    const deliveryMethodMaybe = deliveryMethod ? { deliveryMethod } : {};
    const shippingMethodMaybe = shippingMethod ? { shippingMethodId: shippingMethod } : {};
    const isBrowser = typeof window !== 'undefined';

    // Don't make API calls if we're already in progress or have errors
    if (
      isBrowser &&
      stockReservationQuantity &&
      (!displayDeliveryMethod || deliveryMethod) &&
      !fetchLineItemsInProgress &&
      !fetchLineItemsError // Don't retry if there's an error
    ) {
      // Clear any existing timeout
      if (fetchTimeout) {
        clearTimeout(fetchTimeout);
        setFetchTimeout(null);
      }

      const runFetch = () => {
        if (mounted) {
          onFetchTransactionLineItems({
            orderData: { stockReservationQuantity, ...deliveryMethodMaybe, ...shippingMethodMaybe },
            listingId,
            isOwnListing,
          });
        }
      };

      // Initial load: fetch immediately. Quantity/delivery changes stay debounced.
      if (immediate) {
        runFetch();
      } else {
        const newTimeout = setTimeout(runFetch, 300);
        setFetchTimeout(newTimeout);
      }
    }
  };

  // Calculate stock-related variables early to avoid initialization errors
  const hasNoStockLeft = typeof currentStock != null && currentStock === 0;
  const hasStock = currentStock && currentStock > 0;
  const hasOneItemLeft = currentStock === 1;
  const selectableStock =
    currentStock > MAX_QUANTITY_FOR_DROPDOWN ? MAX_QUANTITY_FOR_DROPDOWN : currentStock;

  const hasInternetReview = hasInternetReviewFromListing(listing);
  const quantities = hasStock ? [...Array(selectableStock).keys()].map(i => i + 1) : [];

  // Allow negative lineTotals for discounts/commissions but require overall customer total > 0
  const validateLineItemsForCart = items => {
    if (!Array.isArray(items) || items.length === 0) return true;
    const numericOk = items.every(
      item =>
        item &&
        item.lineTotal &&
        typeof item.lineTotal.amount === 'number' &&
        !isNaN(item.lineTotal.amount)
    );
    if (!numericOk) return false;
    const customerTotal = items
      .filter(li => (Array.isArray(li.includeFor) ? li.includeFor.includes('customer') : true))
      .reduce((sum, li) => sum + (li.lineTotal?.amount || 0), 0);
    return customerTotal > 0;
  };

  // Validate line items before showing breakdown
  const showBreakdown =
    lineItems &&
    lineItems.length > 0 &&
    !fetchLineItemsInProgress &&
    validateLineItemsForCart(lineItems);
  const breakdownData = lineItems ? lineItems[0] : null;
  const quantityRequiredMsg = intl.formatMessage({ id: 'ProductOrderForm.quantityRequired' });

  // Do not spin the CTA on line-items prefetch — that looked like a second page load.
  const submitInProgress = false;
  // Keep the button enabled regardless of line item fetch state
  const submitDisabled = !hasStock;

  const publicData = listing?.attributes?.publicData || {};

  const handleOnChange = formValues => {
    const { quantity, deliveryMethod, shippingMethod } = formValues.values;
    if (mounted) {
      handleFetchLineItems({
        quantity,
        deliveryMethod,
        shippingMethod,
        displayDeliveryMethod,
        listingId,
        isOwnListing,
        fetchLineItemsInProgress,
        onFetchTransactionLineItems,
      });
    }
  };

  // For cart: directly submit to parent if quantity (and deliveryMethod if needed) is valid
  const handleFormSubmit = e => {
    const { deliveryMethod, shippingMethod } = values || {};
    const finalQuantity = hasOneItemLeft || !allowOrdersOfMultipleItems ? 1 : localQuantity;

    if (!finalQuantity || finalQuantity < 1) {
      e.preventDefault();
      logCartError(CART_ACTIONS.ADD_TO_CART, new Error('Invalid quantity'), {
        context: 'ProductOrderForm',
        finalQuantity,
        localQuantity,
        hasOneItemLeft,
        allowOrdersOfMultipleItems,
      });
      return;
    }
    if (displayDeliveryMethod && !deliveryMethod) {
      e.preventDefault();
      logCartError(CART_ACTIONS.ADD_TO_CART, new Error('Delivery method required'), {
        context: 'ProductOrderForm',
        displayDeliveryMethod,
        deliveryMethod,
        finalQuantity,
      });
      return;
    }

    // Validate line items only if they exist; otherwise proceed to allow cart add
    const hasLineItems = Array.isArray(lineItems) && lineItems.length > 0;
    if (hasLineItems) {
      const hasValidPrices = validateLineItemsForCart(lineItems);
      if (!hasValidPrices) {
        e.preventDefault();
        logCartError(CART_ACTIONS.ADD_TO_CART, new Error('Invalid line item prices'), {
          context: 'ProductOrderForm',
          lineItems,
          hasValidPrices,
        });
        return;
      }
    }

    // Log the form submission attempt
    logAddToCart(
      listing,
      {
        quantity: finalQuantity,
        deliveryMethod,
      },
      {
        context: 'ProductOrderForm',
        formId,
        hasLineItems,
        lineItemsCount: lineItems?.length || 0,
        currentStock,
        hasOneItemLeft,
        allowOrdersOfMultipleItems,
        localQuantity,
        finalQuantity,
        displayDeliveryMethod,
        shippingEnabled,
        pickupEnabled,
      }
    );

    // Submit through Final Form and also call passed handleSubmit directly with current values
    e.preventDefault();
    const formValues = {
      quantity: finalQuantity.toString(),
      deliveryMethod,
      ...(shippingMethod ? { shippingMethodId: shippingMethod } : {}),
    };
    try {
      formApi.change('quantity', formValues.quantity);
      if (deliveryMethod) {
        formApi.change('deliveryMethod', deliveryMethod);
      }
      if (shippingMethod) {
        formApi.change('shippingMethod', shippingMethod);
      }
      // Trigger Final Form submit for validations
      if (typeof formApi.submit === 'function') {
        formApi.submit();
      }
    } catch (error) {
      logCartError(CART_ACTIONS.ADD_TO_CART, error, {
        context: 'ProductOrderForm',
        formValues,
        formApiError: true,
      });
    }
    // Call parent onSubmit directly with formValues including shippingMethodId
    if (onSubmit) {
      onSubmit(formValues);
    }
  };

  const handleQuantityChange = newQuantity => {
    setLocalQuantity(newQuantity);
    formApi.change('quantity', newQuantity.toString());

    // Fetch line items when quantity changes
    if (mounted) {
      handleFetchLineItems({
        quantity: newQuantity.toString(),
        deliveryMethod: values?.deliveryMethod,
        shippingMethod: values?.shippingMethod,
        displayDeliveryMethod,
        listingId,
        isOwnListing,
        fetchLineItemsInProgress,
        onFetchTransactionLineItems,
      });
    }
  };

  // useEffect moved here after all variables and functions are defined
  useEffect(() => {
    setMounted(true);

    const { quantity, deliveryMethod } = values;
    if (quantity) {
      setLocalQuantity(Number.parseInt(quantity, 10) || 1);
    } else if (hasOneItemLeft || !allowOrdersOfMultipleItems) {
      setLocalQuantity(1);
    }

    // Cleanup function to clear timeout on unmount
    return () => {
      if (fetchTimeout) {
        clearTimeout(fetchTimeout);
        setFetchTimeout(null);
      }
    };
  }, [values, hasOneItemLeft, allowOrdersOfMultipleItems, fetchTimeout]); // Simplified dependencies

  // Fallback fetch if loadData did not prefetch (e.g. SSR). Immediate — no debounce.
  useEffect(() => {
    if (mounted && !fetchLineItemsInProgress && !lineItems) {
      const { quantity, deliveryMethod, shippingMethod } = values;
      const initialQuantity = quantity || (hasOneItemLeft || !allowOrdersOfMultipleItems ? 1 : 1);
      const initialDeliveryMethod =
        deliveryMethod ||
        (shippingEnabled
          ? 'shipping'
          : pickupEnabled
          ? 'pickup'
          : displayDeliveryMethod
          ? 'shipping'
          : undefined);

      handleFetchLineItems({
        quantity: initialQuantity.toString(),
        deliveryMethod: initialDeliveryMethod,
        shippingMethod,
        displayDeliveryMethod,
        listingId,
        isOwnListing,
        fetchLineItemsInProgress,
        onFetchTransactionLineItems,
        immediate: true,
      });
    }
  }, [
    mounted,
    values,
    hasOneItemLeft,
    allowOrdersOfMultipleItems,
    displayDeliveryMethod,
    listingId,
    isOwnListing,
    shippingEnabled,
    pickupEnabled,
    fetchLineItemsInProgress,
    lineItems,
    onFetchTransactionLineItems,
  ]);

  // Additional cleanup effect
  useEffect(() => {
    return () => {
      // Clear any pending timeouts when component unmounts
      if (fetchTimeout) {
        clearTimeout(fetchTimeout);
        setFetchTimeout(null);
      }
    };
  }, []); // Empty dependency array - only runs on unmount

  const reviewsCountForRow = Array.isArray(reviews) ? reviews.length : 0;
  const showReviewsVendorRow = hasInternetReview || reviewsCountForRow > 0 || !!listing?.author;

  return (
    <Form onSubmit={handleFormSubmit} className={css.formWrapper}>
      <FormSpy subscription={{ values: true }} onChange={handleOnChange} />

      {/* Reviews count and vendor info on same line */}
      {showReviewsVendorRow ? (
        <div className={css.reviewsAndVendorRow}>
          <ReviewsCount reviews={reviews} intl={intl} hasInternetReview={hasInternetReview} />
          <VendorInfo author={listing?.author} onVendorClick={onOpenSellerPanel} />
        </div>
      ) : null}

      <VariantSelector
        variantsRaw={listing?.attributes?.publicData?.variants}
        currentListingId={listingId}
        mode="desktop"
      />

      <div className={css.formContent}>
        {/* Delivery method selector */}
        {/* <DeliveryMethodMaybe
          displayDeliveryMethod={displayDeliveryMethod}
          hasMultipleDeliveryMethods={hasMultipleDeliveryMethods}
          deliveryMethod={values?.deliveryMethod}
          hasStock={hasStock}
          formId={formId}
          intl={intl}
        /> */}

        {/* Shipping method selector (if delivery is shipping) */}
        {/* DÉPLACÉ VERS LA PAGE PANIER - Le sélecteur de méthode d'expédition est maintenant sur /cart */}
        {/* <ShippingMethodSelector
          listing={listing}
          formId={formId}
          deliveryMethod={values?.deliveryMethod}
          quantity={values?.quantity || localQuantity}
          intl={intl}
          price={price}
        /> */}

        {/* Estimated delivery date */}
        {/* <EstimatedDeliveryDate
          listing={listing}
          shippingMethodId={values?.shippingMethod}
          intl={intl}
        /> */}

        {/* Shipping information - always show if available */}
        {/* <ShippingInfo publicData={publicData} intl={intl} price={price} /> */}

        {/* {showBreakdown ? (
          <div className={css.breakdownWrapper}>
            <H6 as="h3" className={css.bookingBreakdownTitle}>
              <FormattedMessage id="ProductOrderForm.breakdownTitle" />
            </H6>
            <hr className={css.totalDivider} />
            <EstimatedCustomerBreakdownMaybe
              breakdownData={breakdownData}
              lineItems={lineItems}
              currency={price.currency}
              marketplaceName={marketplaceName}
              processName={PURCHASE_PROCESS_NAME}
              listing={listing}
            />
          </div>
        ) : null} */}

        <FetchLineItemsError error={fetchLineItemsError} />

        {/* Manual retry button when there are errors */}
        {fetchLineItemsError && (
          <div className={css.retrySection}>
            <button
              type="button"
              className={css.retryButton}
              onClick={() => {
                const { quantity, deliveryMethod, shippingMethod } = values;
                const currentQuantity =
                  quantity || (hasOneItemLeft || !allowOrdersOfMultipleItems ? 1 : 1);
                const currentDeliveryMethod =
                  deliveryMethod ||
                  (shippingEnabled
                    ? 'shipping'
                    : pickupEnabled
                    ? 'pickup'
                    : displayDeliveryMethod
                    ? 'shipping'
                    : undefined);

                handleFetchLineItems({
                  quantity: currentQuantity.toString(),
                  deliveryMethod: currentDeliveryMethod,
                  shippingMethod,
                  displayDeliveryMethod,
                  listingId,
                  isOwnListing,
                  fetchLineItemsInProgress: false, // Force retry
                  onFetchTransactionLineItems,
                });
              }}
              disabled={fetchLineItemsInProgress}
            >
              {fetchLineItemsInProgress ? (
                <FormattedMessage id="ProductOrderForm.retrying" defaultMessage="Retrying..." />
              ) : (
                <FormattedMessage id="ProductOrderForm.retry" defaultMessage="Retry" />
              )}
            </button>
          </div>
        )}
      </div>

      {/* Delivery methods display */}
      <DeliveryMethodsDisplay listing={listing} price={price} intl={intl} />

      {/* Loyalty points hint – même style que la note livraison */}
      {price && price.amount > 0
        ? (() => {
            const points = Math.floor(price.amount / 100);
            const remaining = Math.max(0, 500 - points);
            return (
              <div className={`${css.deliveryNoteContainer} ${css.loyaltyNoteContainer}`}>
                <p className={css.loyaltyHint}>
                  {points >= 500 ? (
                    <>
                      Vous cumulez{' '}
                      <strong>
                        {points} point{points > 1 ? 's' : ''}
                      </strong>{' '}
                      fidélité et bénéficiez déjà d'un avantage exclusif !
                    </>
                  ) : (
                    <>
                      Vous cumulez{' '}
                      <strong>
                        {points} point{points > 1 ? 's' : ''}
                      </strong>{' '}
                      fidélité. Plus que <strong>{remaining} points</strong> pour bénéficier d'un
                      avantage exclusif !
                    </>
                  )}
                </p>
                <p className={css.loyaltyHint}>
                  🎁
                  {onOpenLoyaltyPanel ? (
                    <button
                      type="button"
                      className={css.loyaltyHintButtonLink}
                      onClick={onOpenLoyaltyPanel}
                    >
                      En savoir plus sur notre programme fidélité
                    </button>
                  ) : (
                    <NamedLink name="ProgrammeFidelitePage" className={css.loyaltyHintLink}>
                      En savoir plus sur notre programme fidélité
                    </NamedLink>
                  )}
                </p>
              </div>
            );
          })()
        : null}

      {/* Quantity selector and Add to Cart button on same line */}
      <div className={css.quantityAndButtonRow}>
        {/* Custom quantity selector */}
        {hasStock && allowOrdersOfMultipleItems && !hasOneItemLeft ? (
          <div className={css.quantitySection}>
            {/* <label className={css.quantityLabel}>
              <FormattedMessage id="ProductOrderForm.quantityLabel" />
            </label> */}
            <QuantitySelector
              quantity={localQuantity}
              onQuantityChange={handleQuantityChange}
              maxQuantity={selectableStock}
              disabled={!hasStock}
            />
          </div>
        ) : hasStock && (hasOneItemLeft || !allowOrdersOfMultipleItems) ? (
          <div className={css.quantitySection}>
            <label className={css.quantityLabel}>
              <FormattedMessage id="ProductOrderForm.quantityLabel" />
            </label>
            <div className={css.singleItemQuantity}>
              <span className={css.quantityDisplay}>1</span>
            </div>
            <FieldTextInput
              id={`${formId}.quantity`}
              className={css.hiddenQuantityField}
              name="quantity"
              type="hidden"
              value="1"
              validate={numberAtLeast(quantityRequiredMsg, 1)}
            />
          </div>
        ) : null}

        <div className={css.submitButton}>
          <PrimaryButton
            type="button"
            onClick={handleFormSubmit}
            inProgress={submitInProgress}
            disabled={submitDisabled}
          >
            {hasStock ? (
              <FormattedMessage id="ProductOrderForm.ctaButtonAddToCart" />
            ) : (
              <FormattedMessage id="ProductOrderForm.ctaButtonNoStock" />
            )}
          </PrimaryButton>
        </div>
      </div>
      <p className={css.finePrint}>
        {payoutDetailsWarning ? (
          payoutDetailsWarning
        ) : hasStock && isOwnListing ? (
          <FormattedMessage id="ProductOrderForm.ownListing" />
        ) : hasStock ? // <FormattedMessage id="ProductOrderForm.finePrint" />
        null : null}
      </p>
    </Form>
  );
};

const ProductOrderForm = props => {
  const intl = useIntl();
  const [mounted, setMounted] = useState(false);
  const [localQuantity, setLocalQuantity] = useState(1);
  const [fetchTimeout, setFetchTimeout] = useState(null); // Add timeout state for debouncing

  const {
    rootClassName,
    className,
    formId,
    listing,
    price,
    currentStock,
    pickupEnabled,
    shippingEnabled,
    allowOrdersOfMultipleItems,
    reviews,
    marketplaceName,
    onSubmit,
    onContactUser,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    listingId,
    isOwnListing,
    onFetchTransactionLineItems,
    displayDeliveryMethod,
  } = props;

  if (displayDeliveryMethod && !pickupEnabled && !shippingEnabled) {
    return (
      <p className={css.error}>
        <FormattedMessage id="ProductOrderForm.noDeliveryMethodSet" />
      </p>
    );
  }

  const hasOneItemLeft = currentStock && currentStock === 1;
  const hasOneItemMode = !allowOrdersOfMultipleItems && currentStock > 0;
  const quantityMaybe = hasOneItemLeft || hasOneItemMode ? { quantity: '1' } : {};

  // Determine default delivery method
  // Priority: shipping > pickup > shipping (fallback if delivery is expected)
  const deliveryMethodMaybe =
    shippingEnabled && pickupEnabled
      ? { deliveryMethod: 'shipping' }
      : shippingEnabled && !pickupEnabled
      ? { deliveryMethod: 'shipping' }
      : !shippingEnabled && pickupEnabled
      ? { deliveryMethod: 'pickup' }
      : displayDeliveryMethod
      ? { deliveryMethod: 'shipping' }
      : {};

  const hasMultipleDeliveryMethods = pickupEnabled && shippingEnabled;

  // Shipping method selection is deterministic: always use first configured method.
  const initialShippingMethods = parseShippingMethods(
    listing?.attributes?.publicData?.shippingMethods
  );
  const shippingMethodMaybe = initialShippingMethods[0]?.id
    ? { shippingMethod: initialShippingMethods[0].id }
    : {};

  const initialValues = { ...quantityMaybe, ...deliveryMethodMaybe, ...shippingMethodMaybe };

  return (
    <FinalForm
      initialValues={initialValues}
      hasMultipleDeliveryMethods={hasMultipleDeliveryMethods}
      displayDeliveryMethod={displayDeliveryMethod}
      {...props}
      intl={intl}
      render={renderForm}
    />
  );
};

export default ProductOrderForm;
